import {
  buttonClasses,
  buttonIconClasses,
} from "@/design-system/Button/Button";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { ReactNode } from "react";

type Size = "small" | "medium" | "large";

/*
  The club green every club-owned thing on these screens wears, over the
  tertiary button's shape. Written through the `custom` variant's own CSS
  variables, so the chip picks up any change to the button skin itself.
*/
const completedSkin = cn(
  "text-sbc-club",
  "[--border-gradient-fill:var(--color-sbc-club-surface)]",
  "[--border-gradient-from:var(--color-sbc-club-border)]",
  "[--border-gradient-to:var(--color-sbc-club-border)]",
);

/**
 * Says a thing is already done, in the shape of the button that would have
 * done it — a club already imported sitting where Import Club sits.
 *
 * Deliberately not a button: there is nothing left to press, and a control
 * that looks pressable but ignores the press is worse than a label. It renders
 * a plain span with no handler, no focus stop and no hover, so a pointer over
 * it says so. Where the action *is* still worth offering, use
 * `<Button variant="tertiary">` and label it for the action.
 */
export const CompletedChip = ({
  children,
  size = "small",
  /** Defaults to a tick; pass another node, or `null` for no icon at all. */
  icon = <Check />,
  className,
}: {
  children: ReactNode;
  size?: Size;
  icon?: ReactNode;
  className?: string;
}) => (
  <span
    className={cn(
      buttonClasses({ variant: "custom", size, className: completedSkin }),
      /* buttonClasses assumes a <button>, which is never the text cursor. */
      "cursor-default select-none",
      className,
    )}
  >
    {icon && <span className={buttonIconClasses({ size })}>{icon}</span>}
    {children}
  </span>
);
